import { VariableTypes, type VariableTypeName } from './variableTypes';
import { VariationTypes } from './variationTypes';

type FeatureFlagBase = {
  // Determines whether the feature flag is send to Fullstory as a user property
  fullstoryEnabled?: boolean;
  name: string;
  variables?: Record<string, VariableTypeName>;
};

export type RolloutFeatureFlag = {
  variationType: typeof VariationTypes.ROLLOUT;
} & FeatureFlagBase;

export type ABTestingFeatureFlag = {
  variationType: typeof VariationTypes.A_B_TESTING;
} & FeatureFlagBase;

export type CustomFeatureFlag = {
  variationType: typeof VariationTypes.CUSTOM;
  variations: readonly string[];
} & FeatureFlagBase;

export type FeatureFlag = ABTestingFeatureFlag | CustomFeatureFlag | RolloutFeatureFlag;

// Names must match the feature flag names in Optimizely
export const FeatureFlags = {
  AMAZON_SHIPPING_ACCOUNT_CONNECTION: {
    fullstoryEnabled: true,
    name: 'amazon_shipping_account_connection',
    variationType: VariationTypes.ROLLOUT,
  },
  AMAZON_SHIPPING_ACCOUNT_REGISTRATION: {
    fullstoryEnabled: true,
    name: 'amazon_shipping_account_registration',
    variationType: VariationTypes.ROLLOUT,
  },
  AUTOMATION_RECOMMENDATIONS: {
    fullstoryEnabled: true,
    name: 'automation_recommendations',
    variationType: VariationTypes.ROLLOUT,
  },
  BEST_RATES_FREQUENCY: {
    fullstoryEnabled: true,
    name: 'best_rates_frequency',
    variables: {
      best_rates_threshold: VariableTypes.DOUBLE,
    },
    variationType: VariationTypes.ROLLOUT,
  },
  BFA_ENABLE_PDF_DOWNLOAD: {
    fullstoryEnabled: true,
    name: 'bfa_enable_pdf_download',
    variationType: VariationTypes.ROLLOUT,
  },
  CCAP_AMAZON_SHIPPING_PICKUP_ENABLED: {
    fullstoryEnabled: true,
    name: 'ccap_amazon_shipping_pickup_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  CHECK_RATES_FULL_ADDRESS: {
    fullstoryEnabled: true,
    name: 'check_rates_full_address',
    variationType: VariationTypes.A_B_TESTING,
  },
  DHL_GERMANY_SUPPORT_DEPRECATED: {
    fullstoryEnabled: false,
    name: 'carrier_support_deprecated',
    variationType: VariationTypes.ROLLOUT,
  },
  DISABLE_RETURN_LABELS_ON_TEST_ORDERS: {
    fullstoryEnabled: true,
    name: 'disable_return_labels_on_test_orders',
    variationType: VariationTypes.ROLLOUT,
  },
  ESTIMATED_DELIVERY_DATE_EXPERIMENT_V2: {
    fullstoryEnabled: true,
    name: 'estimated_delivery_date_experiment_v2',
    variationType: VariationTypes.CUSTOM,
    variations: ['control', 'variant', 'variant_estimate', 'not-included'],
  },
  EU_PRODUCT_IDENTIFICATION: {
    fullstoryEnabled: true,
    name: 'eu_product_identification',
    variationType: VariationTypes.ROLLOUT,
  },
  FEDEX_MCA_VERIFICATION: {
    fullstoryEnabled: false,
    name: 'fedex_account_object_info_verification_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  HUB_OVER_30_DAYS: {
    fullstoryEnabled: true,
    name: 'hub_over_30_days',
    variationType: VariationTypes.A_B_TESTING,
  },
  INSURANCE_LARGE_PACKAGES_UPSELL: {
    fullstoryEnabled: true,
    name: 'insurance_large_packages_upsell',
    variationType: VariationTypes.A_B_TESTING,
  },
  ONBOARDING_2026: {
    fullstoryEnabled: true,
    name: 'onboarding_2026',
    variationType: VariationTypes.CUSTOM,
    variations: ['control', 'variant', 'variant_2', 'not-included'],
  },
  ONTRAC_V3_REGISTRATION_ENABLED: {
    fullstoryEnabled: true,
    name: 'ontrac_v3_registration_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  OPEN_ORDER_DRAWER_ON_BACK: {
    fullstoryEnabled: false,
    name: 'open_order_drawer_on_back',
    variationType: VariationTypes.ROLLOUT,
  },
  ORDER_DETAILS_PAGE_MUI: {
    fullstoryEnabled: true,
    name: 'order_details_page_mui',
    variationType: VariationTypes.ROLLOUT,
  },
  ORDERS_API_V2_UI_VALIDATION: {
    name: 'orders_api_v2_ui_validation',
    variationType: VariationTypes.ROLLOUT,
  },
  ORDERS_TABLE_API_V2_UI: {
    name: 'orders_table_api_v2_ui',
    variationType: VariationTypes.ROLLOUT,
  },
  QUANTITY_FILTER: {
    fullstoryEnabled: true,
    name: 'quantity_filter',
    variationType: VariationTypes.ROLLOUT,
  },
  QUICKPACK_SCAN_TO_PRINT: {
    fullstoryEnabled: true,
    name: 'quickpack_scan_to_print',
    variationType: VariationTypes.ROLLOUT,
  },
  QUICKPACK_SCAN_TUNING_LOGS: {
    name: 'quickpack_scan_tuning_logs',
    variationType: VariationTypes.ROLLOUT,
  },
  QUICKPACK_SHIPPO_PRINT_DETECTION: {
    name: 'quickpack_shippo_print_detection',
    variationType: VariationTypes.ROLLOUT,
  },
  REFERRAL_PROGRAM_EXPERIMENT: {
    fullstoryEnabled: true,
    name: 'referral_program_experiment',
    variationType: VariationTypes.ROLLOUT,
  },
  SENDER_ADDRESS_FILTER: {
    name: 'sender_address_filter',
    variationType: VariationTypes.ROLLOUT,
  },
  SHIPMENT_ALERTS_UI: {
    fullstoryEnabled: true,
    name: 'shipment_alerts_ui',
    variationType: VariationTypes.ROLLOUT,
  },
  SHIPPING_RISK_ASSESSMENT_EXPERIMENT_V2: {
    fullstoryEnabled: true,
    name: 'shipping_risk_assessment_experiment_v2',
    variationType: VariationTypes.A_B_TESTING,
  },
  SHOW_RETAIL_RATES_IN_ONBOARDING: {
    fullstoryEnabled: true,
    name: 'show_retail_rates_in_onboarding',
    variationType: VariationTypes.ROLLOUT,
  },
  SMART_RECOMMENDATIONS: {
    fullstoryEnabled: true,
    name: 'smart_recommendations',
    variationType: VariationTypes.ROLLOUT,
  },
  STORE_SPECIFIC_ORDER_FILTER: {
    name: 'store_specific_order_filter',
    variationType: VariationTypes.ROLLOUT,
  },
  TURN_ON_IBD_ENABLED: {
    fullstoryEnabled: true,
    name: 'turn_on_ibd_xcover_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  USPS_AES_ITN_UI: {
    fullstoryEnabled: true,
    name: 'usps_aes_itn_ui',
    variationType: VariationTypes.ROLLOUT,
  },
  WEBAPP_CARRIER_ADDRESS_VALIDATION_BYPASS: {
    fullstoryEnabled: true,
    name: 'webapp_carrier_address_validation_bypass',
    variationType: VariationTypes.A_B_TESTING,
  },
  WEBAPP_ZENDESK_ENABLED: {
    fullstoryEnabled: true,
    name: 'webapp_zendesk_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  WELCOME_NO_STORE_DESTINATION_TEST: {
    fullstoryEnabled: true,
    name: 'welcome_no_store_destination_test',
    variationType: VariationTypes.CUSTOM,
    variations: ['control', 'hub', 'check_rates', 'not-included'],
  },
  WELCOME_RATES_EXPLORE_COPY_TEST: {
    fullstoryEnabled: true,
    name: 'welcome_rates_explore_copy_test',
    variationType: VariationTypes.A_B_TESTING,
  },
  WELCOME_STORE_DECISION_COPY_TEST: {
    fullstoryEnabled: true,
    name: 'welcome_store_decision_copy_test',
    variationType: VariationTypes.A_B_TESTING,
  },
  ZENDESK_AUTHENTICATION_ENABLED: {
    fullstoryEnabled: true,
    name: 'zendesk_authentication_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
} as const satisfies Record<string, FeatureFlag>;

/**
 * Build a map of feature flags that are enabled for FullStory for easier access
 */
export const FullStoryFeatureFlags = Object.fromEntries(
  Object.entries(FeatureFlags as Record<string, FeatureFlag>).map(
    ([_, { fullstoryEnabled, name }]) => [name, fullstoryEnabled ?? false],
  ),
) as Record<string, boolean>;
